"use client";
import SectionWrapper, {
  basePx,
  basePy,
  SectionWrapperProps,
} from "@cc/section-wrapper";
import ArrowGradient from "@cc/arrow-gradient";
import {
  Theme,
  Typography,
  TypographyProps,
  useMediaQuery,
} from "@mui/material";
import { H5_1, H6_3 } from "@theme/components/typography.fontvariant";
import { deepmerge } from "@mui/utils";
import {
  MotionStack,
  MotionStackProps,
  MotionSvgProps,
  MotionTypography,
  MotionTypographyProps,
} from "./motion-components";
import {
  arrowInfoStaggerDiv,
  arrowInfoStaggerDivChildren,
  arrowLeftToRightTransition,
} from "./animations";
import { HorizontalRule } from "@mui/icons-material";

export interface ObjectiveSectionProps
  extends Omit<SectionWrapperProps, "children"> {
  Title?: string;
  TitleTypographyProps?: Omit<MotionTypographyProps, "children">;
  Objective: string;
  Author?: string;
  Company?: string;
  SVGProps?: MotionSvgProps;
  ObjectiveTypographyProps?: Omit<MotionTypographyProps, "children">;
  AuthorTypographyProps?: Omit<TypographyProps, "children">;
  InfoWrapperProps?: Omit<MotionStackProps, "children">;
}

const ObjectiveSection = (props: ObjectiveSectionProps) => {
  const {
    Title,
    Objective,
    Author,
    Company,
    TitleTypographyProps,
    ObjectiveTypographyProps,
    AuthorTypographyProps,
    SVGProps,
    InfoWrapperProps,
    ...restSectionWrapperProps
  } = props;
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  return (
    <SectionWrapper
      {...deepmerge(defaultSectionProps, restSectionWrapperProps)}
    >
      {isMd && (
        <ArrowGradient
          SVGProps={{
            width: 80,
            height: 210,
            ...SVGProps,
          }}
          {...arrowLeftToRightTransition}
        />
      )}
      <MotionStack
        width={1}
        rowGap={{ xs: 2, md: 3 }}
        variants={arrowInfoStaggerDiv}
        initial={"initial"}
        whileInView={"animate"}
        viewport={{ once: true }}
        {...InfoWrapperProps}
      >
        {Title && (
          <MotionTypography
            component={"h2"}
            variant={"h1"}
            color={"primary.main"}
            textAlign={{ xs: "center", md: "left" }}
            {...TitleTypographyProps}
            variants={arrowInfoStaggerDivChildren}
          >
            {Title}
          </MotionTypography>
        )}
        <MotionTypography
          component={"p"}
          fontSize={H5_1}
          textAlign={{ xs: "center", md: "left" }}
          {...ObjectiveTypographyProps}
          variants={arrowInfoStaggerDivChildren}
        >
          {Objective}
        </MotionTypography>
        {(Author || Company) && (
          <MotionStack
            direction="row"
            variants={arrowInfoStaggerDivChildren}
            columnGap={1}
            justifyContent={{ xs: "center", md: "flex-start" }}
          >
            <HorizontalRule
              fontSize="small"
              sx={{
                mt: { xs: 0.1, xl: 0.5 },
                display: { xs: "none", md: "block" },
              }}
            />
            <MotionStack
              rowGap={{ xs: 0.5, md: 0.75 }}
              textAlign={{ xs: "center", md: "left" }}
            >
              {Author && (
                <Typography
                  component={"p"}
                  fontSize={H6_3}
                  {...AuthorTypographyProps}
                >
                  {Author}
                </Typography>
              )}
              {Company && (
                <Typography
                  component={"p"}
                  fontSize={H6_3}
                  {...AuthorTypographyProps}
                >
                  {Company}
                </Typography>
              )}
            </MotionStack>
          </MotionStack>
        )}
      </MotionStack>
    </SectionWrapper>
  );
};

export default ObjectiveSection;

const defaultSectionProps: Omit<SectionWrapperProps, "children"> = {
  direction: "row",
  color: "secondary.main",
  columnGap: 11,
  alignItems: "center",
  px: { ...basePx, xxl: 25.5 },
  py: { ...basePy, xxl: 8 },
  SectionProps: {
    sx: {
      background: (theme) => theme.palette.gradient.darkToLight,
    },
  },
  navId: "about-us",
};
